import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Plus, Search, Pencil, Trash2, Save, X, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Generic CRUD editor for any whitelisted MongoDB collection.
 * - Lists docs with search + pagination
 * - Modal-based create/edit using JSON editor (works for any schema)
 * - Delete with confirm
 */
export default function CollectionEditor() {
  const { name } = useParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | { mode:'create'|'edit', doc, raw }
  const PAGE = 25;

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/collections/${name}`, {
      params: { skip, limit: PAGE, q },
      withCredentials: true,
    })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch((e) => toast.error("Load failed: " + formatApiError(e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [name, skip, q]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({
      mode: "create",
      doc: {},
      raw: '{\n  \n}',
    });
  };

  const openEdit = (doc) => {
    // strip _id from edit form so admin can't clobber it
    const clean = { ...doc };
    const id = clean._id;
    delete clean._id;
    setEditing({
      mode: "edit",
      doc,
      docId: id,
      raw: JSON.stringify(clean, null, 2),
    });
  };

  const onSave = async () => {
    let payload;
    try {
      payload = JSON.parse(editing.raw);
    } catch (e) {
      toast.error("Invalid JSON: " + e.message);
      return;
    }
    try {
      if (editing.mode === "create") {
        await axios.post(`${API}/admin/collections/${name}`, payload, { withCredentials: true });
        toast.success("Documento creato");
      } else {
        await axios.put(`${API}/admin/collections/${name}/${editing.docId}`, payload, { withCredentials: true });
        toast.success("Documento aggiornato");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed: " + formatApiError(e?.response?.data?.detail || e.message));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo record?")) return;
    try {
      await axios.delete(`${API}/admin/collections/${name}/${id}`, { withCredentials: true });
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error("Delete failed: " + formatApiError(e?.response?.data?.detail || e.message));
    }
  };

  // pick 4 columns to display
  const columns = guessColumns(items);

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/contents" data-testid="back-to-contents"
          className="text-white/40 hover:text-white flex items-center gap-1 text-sm">
          <ArrowLeft size={14} /> Back
        </Link>
        <div className="text-white/30">·</div>
        <h1 className="text-2xl font-black tracking-tight" data-testid="collection-title">{name}</h1>
        <span className="text-white/30 text-sm">{total} record</span>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            placeholder="Cerca…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setSkip(0); }}
            data-testid="search-input"
            className="w-full bg-[#0A0E17] border border-white/10 rounded pl-9 pr-3 py-2 text-sm focus:border-[#00E5FF] outline-none"
          />
        </div>
        <button onClick={load} data-testid="refresh-btn"
          className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded text-sm flex items-center gap-2">
          <RefreshCw size={14} /> Refresh
        </button>
        <button onClick={openCreate} data-testid="create-btn"
          className="px-4 py-2 bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black font-bold rounded text-sm flex items-center gap-2">
          <Plus size={14} /> Nuovo
        </button>
      </div>

      <div className="bg-[#0A0E17] border border-white/10 rounded overflow-hidden" data-testid="docs-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 text-[10px] uppercase tracking-widest border-b border-white/10">
              {columns.map((c) => <th key={c} className="text-left px-4 py-3">{c}</th>)}
              <th className="text-right px-4 py-3 w-32">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-white/40">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-white/40">Nessun record</td></tr>
            )}
            {items.map((d) => (
              <tr key={d._id} data-testid={`row-${d._id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                {columns.map((c) => (
                  <td key={c} className="px-4 py-3 text-white/80 max-w-xs truncate">
                    {formatCell(d[c])}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(d)} data-testid={`edit-${d._id}`}
                    className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]" title="Modifica">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onDelete(d._id)} data-testid={`delete-${d._id}`}
                    className="p-1.5 hover:bg-white/10 rounded text-[#FF3860] ml-1" title="Elimina">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="flex items-center justify-between mt-3 text-sm text-white/40">
        <span>{skip + 1}–{Math.min(skip + PAGE, total)} di {total}</span>
        <div className="flex gap-2">
          <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded disabled:opacity-30">‹ Prev</button>
          <button disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded disabled:opacity-30">Next ›</button>
        </div>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-black">
              {editing.mode === "create" ? "Nuovo record" : "Modifica record"}
            </h2>
            <button onClick={() => setEditing(null)} className="text-white/40 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="text-white/40 text-xs mb-2">
            Edita il JSON e premi Salva. Per le date usa formato ISO. _id è gestito automaticamente.
          </div>
          <textarea
            value={editing.raw}
            onChange={(e) => setEditing({ ...editing, raw: e.target.value })}
            data-testid="json-editor"
            className="w-full h-96 bg-black/60 border border-white/10 rounded p-3 font-mono text-xs text-white/90 focus:border-[#00E5FF] outline-none"
            spellCheck={false}
          />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} data-testid="cancel-btn"
              className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded text-sm">Annulla</button>
            <button onClick={onSave} data-testid="save-btn"
              className="px-4 py-2 bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black font-bold rounded text-sm flex items-center gap-2">
              <Save size={14} /> Salva
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function guessColumns(items) {
  if (!items.length) return ["_id", "name", "title", "type"];
  const seen = new Set();
  for (const it of items) {
    Object.keys(it).forEach((k) => seen.add(k));
  }
  const preferred = ["_id", "id", "name", "title", "email", "type", "company", "category", "location", "client_id"];
  const ordered = preferred.filter((p) => seen.has(p));
  for (const k of seen) {
    if (!ordered.includes(k) && ordered.length < 5) ordered.push(k);
  }
  return ordered.slice(0, 5);
}

function formatCell(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  if (typeof v === "boolean") return v ? "✓" : "✗";
  const s = String(v);
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-8" onClick={onClose}>
      <div className="bg-[#0A0E17] border border-white/10 rounded-lg p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
